// A preset's tile preview (#266, spec animations.md decision 7): live's tiles loop a video of each
// preset, hot-linked from ColorTV's bucket; ours plays the preset's own motion (the engine's
// `motionAt` over its stops) on a small picture drawn by the page, with the browser's animations,
// so every tile of the grid moves at once without a canvas each.
import { useEffect, useRef } from "react";

import type { AnimationPreset } from "../../api/generated/model";
import { PRESETS, newAnimation } from "./animations";
import { motionAt, type Motion } from "./engine/animate";
import { maskShape } from "./engine/mask-shape";
import classes from "./animations.module.css";

/** A tile's loop: a transition plays for a second and holds; a combo runs two seconds. */
const TRANSITION_MS = 1000;
const HOLD_MS = 600;
const COMBO_MS = 2000;
const FRAMES = 24;
/** The tile stands for live's 1080-pixel frame. */
const TILE_UNIT = 1 / 1080;

/** One frame of the motion as CSS, on a tile one unit square (percentages of it). */
function frameOf(motion: Motion): Keyframe {
  const frame: Keyframe = {
    transform: `translate(${motion.x * TILE_UNIT * 100}%, ${motion.y * TILE_UNIT * 100}%) rotate(${motion.angle}deg) scale(${motion.scaleX}, ${motion.scaleY})`,
    opacity: motion.opacity,
    filter: `blur(${(motion.blur + motion.motionBlur / 4) * TILE_UNIT * 72}px) brightness(${motion.brightness}) saturate(${motion.saturation})`,
  };
  if (motion.mask !== null && motion.mask.progress < 1) {
    // The mask on a 100 by 100 body about its centre, as a clip path in percent.
    const shape = maskShape(motion.mask, 100, 100, 100 * TILE_UNIT, Math.max(motion.scaleX, motion.scaleY));
    const at = (value: number) => `${value + 50}%`;
    if (shape.kind === "circle") frame.clipPath = `circle(${shape.radius}% at ${at(shape.x)} ${at(shape.y)})`;
    else if (shape.kind === "box") {
      const round = shape.radius > 0 ? ` round ${shape.radius}%` : "";
      frame.clipPath = `inset(${at(shape.y)} ${50 - shape.x - shape.width}% ${50 - shape.y - shape.height}% ${at(shape.x)}${round})`;
    } else {
      frame.clipPath =
        shape.points.length === 0 ? "inset(50%)" : `polygon(${shape.points.map((p) => `${at(p.x)} ${at(p.y)}`).join(", ")})`;
    }
  } else {
    frame.clipPath = "inset(0%)";
  }
  return frame;
}

/** The preset's tile: its motion over the sample picture, again and again. */
export function PresetPreview({ preset }: { preset: AnimationPreset }) {
  const sample = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = sample.current;
    if (element === null || typeof element.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const combo = PRESETS[preset].group === "combo";
    const runMs = combo ? COMBO_MS : TRANSITION_MS;
    const totalMs = combo ? COMBO_MS : TRANSITION_MS + HOLD_MS;
    const animation = { ...newAnimation("preview", preset, runMs), durationMs: runMs };
    const frames = Array.from({ length: FRAMES + 1 }, (_, at) => {
      const ms = (at / FRAMES) * totalMs;
      return { ...frameOf(motionAt([animation], ms, totalMs)), offset: at / FRAMES };
    });
    const playing = element.animate(frames, { duration: totalMs, iterations: Number.POSITIVE_INFINITY });
    return () => playing.cancel();
  }, [preset]);
  return (
    <span className={classes.tilePreview} aria-hidden>
      <div ref={sample} className={classes.sample} />
    </span>
  );
}
